"use client";

import {
  useState,
  useEffect,
  useCallback,
  startTransition,
  Suspense,
  type Dispatch,
  type SetStateAction,
} from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import type { KundaliResponse } from "@/lib/api/kundali";
import { useDeliveryCapture } from "./KundaliFormCard.delivery";
import { makeHandleSubmit } from "./KundaliFormCard.submit";
import type { PlaceResult } from "@/lib/api/misc";
// Kundali-form analytics imports go straight to the source module instead of
// re-routing through `@/lib/analytics`. The barrel re-export there
// (`export * from "@/lib/analytics-kundali"`) created a chunk-resolution race
// in production builds — `(0, o.trackKundaliFormStarted) is not a function`
// (Sentry 7481455768) fired when the consumer chunk loaded before the kundali
// chunk hydrated the namespace object. Direct imports avoid the indirection.
import {
  trackKundaliFormStarted,
  trackKundaliFormSubmitFailed,
  trackKundaliGenerated,
  trackKundaliResultViewed,
} from "@/lib/analytics-kundali";
import { DailyInsightSlot } from "./KundaliFormCard.controls";
import { KundaliInputForm, type CapturedSEOEntry } from "./KundaliFormCard.parts";
import { getKundaliTranslations, type KundaliLang } from "./KundaliFormCard.i18n";
import { LOADING_STEPS, STEP_DELAY_MS } from "./KundaliFormCard.loading";
import { useCapturedSEOEntry } from "./KundaliFormCard.seo";
import type { KundaliFieldErrors } from "./KundaliFormCard.validation";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import DailyPanchangLink from "@/components/DailyPanchangLink";
import KundaliSavedLink from "./KundaliSavedLink";
import { rememberLastReading } from "@/lib/last-reading";

const SpiritualReveal = dynamic(() => import("@ishvaram/ui/SpiritualReveal"), { ssr: false });
const KundaliResult = dynamic(() => import("@/components/KundaliResult"), { ssr: false });

type KundaliFormCardProps = {
  defaultLanguage?: KundaliLang;
  source?: string;
  submitLabel?: string;
  savedNote?: string;
  showDailyInsightCard?: boolean;
  containerClassName?: string;
  namePlaceholder?: string;
  placePlaceholder?: string;
  requireBirthTime?: boolean;
  reassuranceNote?: string;
  reassuranceNoteHi?: string;
  loadingLabel?: string;
  loadingLabelHi?: string;
  seoEntryOverride?: CapturedSEOEntry | null;
};

export default function KundaliFormCard({
  defaultLanguage = "en",
  source = "home",
  submitLabel,
  savedNote,
  showDailyInsightCard = true,
  containerClassName = "mx-auto max-w-md px-4 sm:px-6 pb-16",
  namePlaceholder,
  placePlaceholder,
  requireBirthTime = false,
  reassuranceNote,
  reassuranceNoteHi,
  loadingLabel,
  loadingLabelHi,
  seoEntryOverride,
}: KundaliFormCardProps) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<KundaliFieldErrors>({});
  const [result, setResult] = useState<KundaliResponse | null>(null);
  const [language, setLanguage] = useState<KundaliLang>(defaultLanguage);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [formStarted, setFormStarted] = useState(false);
  const loadingStep = useLoadingStep(loading);
  const showDailyInsight = useDeferredDailyInsight();
  const capturedSeoEntry = useCapturedSEOEntry(seoEntryOverride);
  const seoEntry = seoEntryOverride !== undefined ? seoEntryOverride : capturedSeoEntry;
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [birthPlace, setBirthPlace] = useState("");
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);
  const delivery = useDeliveryCapture(language); // form-level delivery capture (ADR-0026)
  const updateField = makeFieldUpdater(setError, setFieldErrors);
  const markFormStarted = useMarkFormStarted({
    setActiveField,
    formStarted,
    setFormStarted,
    language,
    seoEntry,
    source,
  });

  const handleSubmit = makeHandleSubmit({
    seoEntry,
    source,
    requireBirthTime,
    name,
    birthDate,
    birthTime,
    birthPlace,
    language,
    selectedPlace,
    delivery,
    router,
    setLoading,
    setError,
    setFieldErrors,
    setResult,
  });

  if (result) return renderKundaliResult(result, language, source, () => setResult(null));

  const t = getKundaliTranslations(language, {
    submitLabel,
    savedNote,
    loadingLabel: localizedOverride(language, loadingLabel, loadingLabelHi),
    namePlaceholder,
    placePlaceholder,
  });
  const localizedReassuranceNote = localizedOverride(language, reassuranceNote, reassuranceNoteHi);

  return (
    <>
      <KundaliSpiritualReveal loading={loading} language={language} loadingStep={loadingStep} />
      <KundaliInputForm
        containerClassName={containerClassName}
        onSubmit={handleSubmit}
        seoEntry={seoEntry}
        language={language}
        setLanguage={setLanguage}
        name={name}
        setName={(value) => updateField("name", value, setName)}
        birthDate={birthDate}
        setBirthDate={(value) => updateField("birthDate", value, setBirthDate)}
        birthTime={birthTime}
        setBirthTime={setBirthTime}
        requireBirthTime={requireBirthTime}
        birthPlace={birthPlace}
        setBirthPlace={(value) => updateField("birthPlace", value, setBirthPlace)}
        setSelectedPlace={setSelectedPlace}
        activeField={activeField}
        setActiveField={setActiveField}
        markFormStarted={markFormStarted}
        fieldErrors={fieldErrors}
        error={error}
        loading={loading}
        loadingStep={loadingStep}
        reassuranceNote={localizedReassuranceNote}
        deliveryField={delivery.field}
        t={t}
      />
      <DailyInsightSlot show={showDailyInsightCard} ready={showDailyInsight} language={language} />
    </>
  );
}

function localizedOverride(language: KundaliLang, en?: string, hi?: string): string | undefined {
  return language === "hi" ? (hi ?? en) : en;
}

export function currentPathname(): string | undefined {
  return typeof window !== "undefined" ? window.location.pathname || undefined : undefined;
}

function focusInvalidField(field: keyof KundaliFieldErrors) {
  window.setTimeout(() => {
    document.getElementById(field)?.focus();
    document.getElementById(field)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 0);
}

export function handleInvalidKundaliForm({
  validation,
  setFieldErrors,
  setError,
  language,
  birthTime,
  analyticsSource,
  sourcePath,
}: {
  validation: { errors: KundaliFieldErrors; firstField: keyof KundaliFieldErrors; ok: false };
  setFieldErrors: Dispatch<SetStateAction<KundaliFieldErrors>>;
  setError: Dispatch<SetStateAction<string | null>>;
  language: KundaliLang;
  birthTime: string;
  analyticsSource: string;
  sourcePath: string | undefined;
}) {
  setFieldErrors(validation.errors);
  setError(validation.errors[validation.firstField] || "Please check the form.");
  focusInvalidField(validation.firstField);
  trackKundaliFormSubmitFailed({
    language,
    has_time: !!birthTime,
    source: analyticsSource,
    source_path: sourcePath,
    error_message: validation.firstField,
  });
}

function useMarkFormStarted(args: {
  setActiveField: (field: string) => void;
  formStarted: boolean;
  setFormStarted: (value: boolean) => void;
  language: KundaliLang;
  seoEntry: CapturedSEOEntry | null;
  source: string;
}) {
  const { setActiveField, formStarted, setFormStarted, language, seoEntry, source } = args;
  return useCallback(
    (firstField: string) => {
      setActiveField(firstField);
      if (formStarted) return;
      setFormStarted(true);
      trackKundaliFormStarted({
        language,
        first_field: firstField,
        source: seoEntry ? `seo:${seoEntry.config.intent}` : source,
        source_path:
          seoEntry?.sourcePath ||
          (typeof window !== "undefined" ? window.location.pathname || undefined : undefined),
      });
    },
    [setActiveField, formStarted, setFormStarted, language, seoEntry, source]
  );
}

function makeFieldUpdater(
  setError: Dispatch<SetStateAction<string | null>>,
  setFieldErrors: Dispatch<SetStateAction<KundaliFieldErrors>>
) {
  return function updateField<K extends keyof KundaliFieldErrors>(
    field: K,
    value: string,
    setter: (next: string) => void
  ) {
    setter(value);
    setError(null);
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };
}

function renderKundaliResult(
  result: KundaliResponse,
  language: KundaliLang,
  source: string,
  onBack: () => void
) {
  const layout = source === "ai_astrologer" ? "chat-first" : "result-first";
  const content = (
    <KundaliResultBoundary result={result} language={language} onBack={onBack} layout={layout} />
  );
  if (layout !== "chat-first") {
    // Result-first entries keep the inline result AND get its durable return
    // path: every generated kundali is persisted under result.id, and
    // /kundali/<id> re-renders the same KundaliResult tree (rule 46 — the
    // inline value moment stays put; the saved link is additive).
    return (
      <>
        {content}
        {result.id ? <KundaliSavedLink readingId={result.id} language={language} /> : null}
        {result.id ? (
          <DailyPanchangLink
            readingId={result.id}
            language={language === "hi" ? "hi" : "en"}
            source="kundali_result"
          />
        ) : null}
      </>
    );
  }
  return <div className="ai-chat-result-active">{content}</div>;
}

function KundaliResultBoundary({
  result,
  language,
  onBack,
  layout,
}: {
  result: KundaliResponse;
  language: KundaliLang;
  onBack: () => void;
  layout: "result-first" | "chat-first";
}) {
  return (
    <ErrorBoundary>
      {/* Suspense boundary is required: KundaliResult is a dynamic(ssr:false)
          client component that calls useSearchParams(); without a boundary the
          searchParams read deopts the render and the chunk-load placeholder vs
          loaded component differ in hook count → "Rendered more hooks than
          during the previous render" crashes on /ai-astrologer + /free-kundali. */}
      <Suspense fallback={null}>
        <KundaliResult result={result} language={language} onBack={onBack} layout={layout} />
      </Suspense>
    </ErrorBoundary>
  );
}

// Chat-first entries hand off to the dedicated /chat/<id> surface; result-first
// entries (free-kundali and tool pages) keep the inline result + chart. Add a
// page's KundaliFormCard `source` here to route it into the dedicated chat.
const CHAT_FIRST_SOURCES = new Set(["ai_astrologer", "free_astrology_chat", "nadi_astrology"]);
function isChatFirstSource(source: string): boolean {
  return CHAT_FIRST_SOURCES.has(source);
}

/**
 * Post-generation handoff: persist + track, then either redirect a chat-first
 * entry to the dedicated /chat/<id> surface (returns true = navigated, so the
 * caller keeps the loading reveal through the transition) or swap the inline
 * result for result-first entries.
 */
export function finishKundali(
  response: KundaliResponse,
  ctx: {
    source: string;
    language: KundaliLang;
    birthTime: string;
    analyticsSource: string;
    sourcePath: string | undefined;
  },
  router: ReturnType<typeof useRouter>,
  setResult: (response: KundaliResponse) => void
): boolean {
  rememberLastReading({
    id: response.id,
    language: ctx.language,
    name: (response.birth_details as { name?: string } | undefined)?.name,
  });
  trackSuccessfulKundali(
    response,
    ctx.language,
    ctx.birthTime,
    ctx.analyticsSource,
    ctx.sourcePath
  );
  if (isChatFirstSource(ctx.source)) {
    router.push(`/chat/${response.id}?lang=${ctx.language}`);
    return true;
  }
  setResult(response);
  return false;
}

function trackSuccessfulKundali(
  response: KundaliResponse,
  language: KundaliLang,
  birthTime: string,
  source: string,
  sourcePath: string | undefined
) {
  trackKundaliGenerated({
    language,
    has_time: !!birthTime,
    source,
    source_path: sourcePath,
  });
  trackKundaliResultViewed({
    reading_id: response.id,
    language,
    has_time: !!birthTime,
    source,
    source_path: sourcePath,
    dosha_count: response.doshas.length,
    product_count: response.product_recommendations.length,
  });
}

function useDeferredDailyInsight() {
  const [showDailyInsight, setShowDailyInsight] = useState(false);
  useEffect(() => {
    const load = () => startTransition(() => setShowDailyInsight(true));
    const idle = globalThis.requestIdleCallback;
    const id =
      typeof idle === "function"
        ? idle(load, { timeout: 5000 })
        : globalThis.setTimeout(load, 5000);
    return () => cancelDeferredDailyInsight(id);
  }, []);
  return showDailyInsight;
}

function cancelDeferredDailyInsight(id: number) {
  if (typeof globalThis.cancelIdleCallback === "function") {
    globalThis.cancelIdleCallback(id);
  } else {
    globalThis.clearTimeout(id);
  }
}

function useLoadingStep(loading: boolean) {
  const [loadingStep, setLoadingStep] = useState(0);
  useEffect(() => {
    startTransition(() => setLoadingStep(0));
    if (!loading) return;
    const timers = loadingTimers(setLoadingStep);
    return () => timers.forEach(clearTimeout);
  }, [loading]);
  return loadingStep;
}

function loadingTimers(setLoadingStep: (step: number) => void) {
  const timers: ReturnType<typeof setTimeout>[] = [];
  for (let i = 1; i < LOADING_STEPS.length; i++) {
    timers.push(setTimeout(() => startTransition(() => setLoadingStep(i)), STEP_DELAY_MS * i));
  }
  return timers;
}

function KundaliSpiritualReveal({
  loading,
  language,
  loadingStep,
}: {
  loading: boolean;
  language: KundaliLang;
  loadingStep: number;
}) {
  if (!loading) return null;
  return (
    <SpiritualReveal loading language={language} steps={LOADING_STEPS} currentStep={loadingStep} />
  );
}
